import { inject, Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { BaseSignalRNotification, DataStoreSignalRNotification, WorkspaceSignalRNotification } from '../models/signal-r-notification.model';
import { DataStoreService } from '../../features/tenant-management/tabs/data-lakes-tab/services/data-store.service';
import { WorkspaceService } from '../../features/tenant-management/tabs/workspaces-tab/services/workspace.service';
import { TenantContextService } from '../../features/tenant-management/services/tenant-context.service';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private connection: signalR.HubConnection | null = null;

  private msalService = inject(MsalService);
  private tenantContextService = inject(TenantContextService);
  private dataStoreService = inject(DataStoreService);
  private workspaceService = inject(WorkspaceService);

  public connect(): void {
    if (this.connection) return;
    
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalRHub.serviceBaseUrl, { accessTokenFactory: () => this.getAccessToken()})
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.registerHandlers();
    this.startConnection();
  }

  private async startConnection() {
    try {
      await this.connection!.start();
      console.log("SignalR connection established.");
    } catch (err) {
      console.error("SignalR connection error: ", err);
      setTimeout(() => this.startConnection(), 5000);
    }
  }

  private registerHandlers() {
    this.connection!.on("heartbeat", (message: string) => {
      console.log("Heartbeat response from server:", message);
    });

    this.connection!.on("GlobalNotification", (notification: BaseSignalRNotification) => {
      console.log("Notification recived");

      const currentTenantId = this.tenantContextService.tenant()?.tenantId;

      switch (notification.UIStateName) {
        case "DataLakesTab":
          this.dataStoreService.handleDataStoreUpdate(notification as DataStoreSignalRNotification);
          break;
        case "WorkspacesTab":
          this.workspaceService.handleWorkspaceUpdate(notification as WorkspaceSignalRNotification, currentTenantId);
          break;
      }
    });
  }

  public sendHeartbeat(message: string) {
    if (!this.connection) {
      console.warn('SignalR is not connected');
      return;
    }

    this.connection
      .invoke("Heartbeat", message)
      .then(() => console.log("Heartbeat sent:", message))
      .catch(err => console.error("Error sending heartbeat:", err));
  }

  public disconnect(): void {
    if (this.connection) {
      this.connection.stop();
      this.connection = null;
      console.log("SignalR connection disconnected.");
    }
  }

  private async getAccessToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      return Promise.reject("No active MSAL account");
    }

    const result = await this.msalService.instance.acquireTokenSilent({
      scopes: environment.appScopes,
      account
    });
    return result.accessToken;
  }
}
